import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Fade,
  CircularProgress,
  Snackbar,
  Button
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import WarningIcon from '@material-ui/icons/Warning';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import DirectionsCarIcon from '@material-ui/icons/DirectionsCar';
import ScheduleIcon from '@material-ui/icons/Schedule';
import LocalGasStationIcon from '@material-ui/icons/LocalGasStation';
import axios from 'axios';

import EntitySelector from './components/EntitySelector/EntitySelector';
import DivisionFilter from './components/DivisionFilter/DivisionFilter';
import CrewCard from './components/CrewCard/CrewCard';
import AlertsPanel from './components/AlertsPanel/AlertsPanel';
import SiteReviewMigration from './SiteReviewMigration';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: theme.spacing(3),
    backgroundColor: '#f5f5f5',
    minHeight: '100vh'
  },
  header: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2)
  },
  title: {
    color: theme.palette.primary.dark,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  headerControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  summarySection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2]
  },
  summaryStats: {
    display: 'flex',
    gap: theme.spacing(3),
    flexWrap: 'wrap',
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1)
    }
  },
  statChip: {
    fontWeight: 600,
    fontSize: '0.9rem',
    padding: theme.spacing(1, 2)
  },
  alertsContainer: {
    marginBottom: theme.spacing(3)
  },
  refreshButton: {
    color: theme.palette.primary.main
  },
  crewGrid: {
    marginTop: theme.spacing(2)
  },
  statusIcon: {
    verticalAlign: 'middle'
  },
  greenChip: {
    backgroundColor: '#4caf50',
    color: '#fff'
  },
  yellowChip: {
    backgroundColor: '#ff9800',
    color: '#fff'
  },
  redChip: {
    backgroundColor: '#f44336',
    color: '#fff'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    gap: theme.spacing(2)
  },
  errorContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  lastUpdated: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem'
  }
}));

const AdminDashboard = ({ entities, defaultEntity, user }) => {
  const classes = useStyles();
  
  // Initialize with default entity if available, otherwise 'all'
  const [selectedEntities, setSelectedEntities] = useState(
    defaultEntity ? [defaultEntity] : ['all']
  );
  const [selectedDivisions, setSelectedDivisions] = useState(['all']);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({ 
    crews: [], 
    alerts: [], 
    summary: null,
    meta: null 
  });
  const [lastUpdated, setLastUpdated] = useState(null);
  const dashboardDataRef = useRef(dashboardData);
  dashboardDataRef.current = dashboardData;

  // Fetch dashboard snapshot from API
  const fetchDashboardSnapshot = useCallback(async (forceRefresh = false) => {
    try {
      setError(null);
      const baseUrl = process.env.REACT_APP_HOST
        ? process.env.REACT_APP_PROTOCOL + '://' + process.env.REACT_APP_HOST
        : window.location.origin;

      // Build query parameters
      const params = new URLSearchParams();

      // Add entity filter
      if (!selectedEntities.includes('all')) {
        selectedEntities.forEach(entityId => {
          params.append('entities', entityId);
        });
      }

      // Add division filter
      if (!selectedDivisions.includes('all')) {
        selectedDivisions.forEach(division => {
          params.append('divisions', division);
        });
      }

      // Force refresh if requested
      if (forceRefresh) {
        params.append('force', 'true');
      }

      const response = await axios.get(`${baseUrl}/api/admin/dashboard/snapshot?${params.toString()}`);

      setDashboardData({
        crews: response.data.crews || [],
        alerts: response.data.alerts || [],
        summary: response.data.summary || {},
        meta: response.data.meta || {}
      });

      setLastUpdated(new Date());

    } catch (err) {
      console.error('Error fetching dashboard snapshot:', err);
      setError(err.response?.data?.message || 'Failed to fetch dashboard data');

      // Keep existing data if available
      if (!dashboardDataRef.current.crews.length) {
        setDashboardData({
          crews: [],
          alerts: [],
          summary: null,
          meta: null
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedEntities, selectedDivisions]);

  // Auto-refresh interval (60 seconds)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchDashboardSnapshot(false);
    }, 60000);

    return () => clearInterval(interval);
  }, [fetchDashboardSnapshot]);

  // Initial load and when filters change
  useEffect(() => {
    setLoading(true);
    fetchDashboardSnapshot();
  }, [fetchDashboardSnapshot]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardSnapshot(true); // Force refresh
  };

  const handleEntityChange = (newSelection) => {
    setSelectedEntities(newSelection);
  };

  const handleDivisionChange = (newSelection) => {
    setSelectedDivisions(newSelection);
  };

  // Extract unique divisions from crews
  const uniqueDivisions = React.useMemo(() => {
    const divisions = new Set();
    dashboardData.crews?.forEach(crew => {
      if (crew.division) {
        divisions.add(crew.division);
      }
    });
    return Array.from(divisions).sort();
  }, [dashboardData.crews]);

  // Use summary from API or calculate from crews
  const summaryStats = dashboardData.summary || {
    totalCrews: dashboardData.crews.length,
    activeCrews: dashboardData.crews.filter(c => c.status === 'active').length,
    travelingCrews: dashboardData.crews.filter(c => c.status === 'traveling').length,
    idleCrews: dashboardData.crews.filter(c => c.status === 'idle').length,
    breakCrews: dashboardData.crews.filter(c => c.status === 'break').length,
    avgEfficiency: Math.round(
      dashboardData.crews.reduce((acc, c) => acc + (c.todayMetrics?.performance?.efficiency || 0), 0) / 
      (dashboardData.crews.length || 1)
    ),
    lowFuelCount: dashboardData.crews.filter(c => (c.vehicle?.metrics?.fuelLevel || 100) < 30).length
  };

  const formatLastUpdated = () => {
    if (!lastUpdated) return '';
    const now = new Date();
    const diffMs = now - lastUpdated;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 minute ago';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  return (
    <Container maxWidth={false} className={classes.root}>
      {/* Header Section */}
      <Box className={classes.header}>
        <Box>
          <Typography variant="h4" className={classes.title}>
            Admin Dashboard
            <Chip 
              label={dashboardData.meta?.isStale ? "STALE" : "LIVE"} 
              color={dashboardData.meta?.isStale ? "default" : "primary"}
              size="small"
              icon={dashboardData.meta?.isStale ? <WarningIcon /> : <CheckCircleIcon />}
            />
          </Typography>
          {lastUpdated && (
            <Typography variant="caption" className={classes.lastUpdated}>
              Last updated: {formatLastUpdated()}
            </Typography>
          )}
        </Box>
        
        <Box className={classes.headerControls}>
          <EntitySelector
            entities={entities}
            selectedEntities={selectedEntities}
            onChange={handleEntityChange}
          />
          <DivisionFilter
            divisions={uniqueDivisions}
            selectedDivisions={selectedDivisions}
            onChange={handleDivisionChange}
          />
          <Tooltip title="Refresh Dashboard">
            <IconButton 
              onClick={handleRefresh}
              className={classes.refreshButton}
              disabled={refreshing}
            >
              <RefreshIcon className={refreshing ? 'rotating' : ''} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Paper className={classes.errorContainer} style={{ 
          padding: 16, 
          backgroundColor: '#ffebee',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ErrorIcon style={{ color: '#f44336' }} />
            <Typography style={{ color: '#d32f2f' }}>
              {error}
            </Typography>
          </Box>
          <Button 
            size="small" 
            onClick={() => fetchDashboardSnapshot()}
            style={{ color: '#d32f2f' }}
          >
            Retry
          </Button>
        </Paper>
      )}

      {/* Loading State */}
      {loading && !error ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">
            Loading dashboard data...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Summary Statistics */}
          <Paper className={classes.summarySection}>
            <Typography variant="h6" gutterBottom>
              Fleet Overview
            </Typography>
            <Box className={classes.summaryStats}>
              <div>
                <Typography variant="body2" color="textSecondary">Total Crews</Typography>
                <Chip label={summaryStats.totalCrews || 0} className={classes.statChip} />
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Active</Typography>
                <Chip 
                  label={summaryStats.activeCrews || 0} 
                  className={`${classes.statChip} ${classes.greenChip}`}
                  icon={<CheckCircleIcon />}
                />
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Traveling</Typography>
                <Chip 
                  label={summaryStats.travelingCrews || 0} 
                  className={`${classes.statChip}`}
                  style={{ backgroundColor: '#2196f3', color: '#fff' }}
                  icon={<DirectionsCarIcon />}
                />
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Idle</Typography>
                <Chip 
                  label={summaryStats.idleCrews || 0} 
                  className={`${classes.statChip} ${classes.redChip}`}
                  icon={<ErrorIcon />}
                />
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Break</Typography>
                <Chip 
                  label={summaryStats.breakCrews || 0} 
                  className={`${classes.statChip} ${classes.yellowChip}`}
                  icon={<ScheduleIcon />}
                />
              </div>
              <div>
                <Typography variant="body2" color="textSecondary">Avg Efficiency</Typography>
                <Chip 
                  label={`${summaryStats.avgEfficiency || 0}%`} 
                  className={classes.statChip}
                  icon={summaryStats.avgEfficiency > 80 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                />
              </div>
              {(summaryStats.lowFuelCount || summaryStats.lowFuelCrews) > 0 && (
                <div>
                  <Typography variant="body2" color="textSecondary">Low Fuel</Typography>
                  <Chip 
                    label={summaryStats.lowFuelCount || summaryStats.lowFuelCrews || 0} 
                    className={`${classes.statChip}`}
                    style={{ backgroundColor: '#ff5722', color: '#fff' }}
                    icon={<LocalGasStationIcon />}
                  />
                </div>
              )}
            </Box>
          </Paper>

          {/* Overall Time Metrics - Only show if we have the data */}
          {dashboardData.summary?.timeMetrics && (
            <Paper className={classes.summarySection}>
              <Typography variant="h6" gutterBottom>
                Fleet Time Analysis
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box style={{ textAlign: 'center', marginBottom: 16 }}>
                    <Typography variant="h3" style={{ color: '#4caf50', fontWeight: 600 }}>
                      {dashboardData.summary.timeMetrics.overallUtilization || 0}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Overall Utilization Rate
                    </Typography>
                  </Box>
                  
                  {/* Visual bar showing working vs idle time */}
                  <Box style={{ 
                    height: 40, 
                    backgroundColor: '#e0e0e0', 
                    borderRadius: 8,
                    position: 'relative',
                    overflow: 'hidden',
                    marginBottom: 8
                  }}>
                    <Box style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${dashboardData.summary.timeMetrics.overallUtilization || 0}%`,
                      backgroundColor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#fff',
                      fontWeight: 600
                    }}>
                      {dashboardData.summary.timeMetrics.overallUtilization > 10 && 'Working'}
                    </Box>
                    <Box style={{
                      position: 'absolute',
                      right: 0,
                      top: 0,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#666',
                      fontWeight: 600,
                      paddingRight: 16
                    }}>
                      {(100 - (dashboardData.summary.timeMetrics.overallUtilization || 0)) > 20 && 'Idle/Other'}
                    </Box>
                  </Box>
                  
                  <Box style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                    <span>{dashboardData.summary.timeMetrics.totalWorkingHours}h working</span>
                    <span>{dashboardData.summary.timeMetrics.totalIdleHours}h idle/other</span>
                  </Box>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Paper style={{ padding: 16, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="h5" style={{ fontWeight: 600 }}>
                          {dashboardData.summary.timeMetrics.totalClockedHours || 0}h
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Total Clocked Time
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper style={{ padding: 16, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
                        <Typography variant="h5" style={{ fontWeight: 600, color: '#4caf50' }}>
                          {dashboardData.summary.workorderMetrics?.completionRate || 0}%
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Jobs Completed
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper style={{ padding: 16, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <Typography variant="h5" style={{ fontWeight: 600 }}>
                          {dashboardData.summary.workorderMetrics?.totalAssigned || 0}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Jobs Assigned
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper style={{ padding: 16, textAlign: 'center', backgroundColor: '#e8f5e9' }}>
                        <Typography variant="h5" style={{ fontWeight: 600, color: '#4caf50' }}>
                          {dashboardData.summary.workorderMetrics?.totalCompleted || 0}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Jobs Completed
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* Alerts Section */}
          {dashboardData.alerts.length > 0 && (
            <Box className={classes.alertsContainer}>
              <AlertsPanel alerts={dashboardData.alerts} />
            </Box>
          )}

          {/* Site Review Migration Tool */}
          <Box className={classes.alertsContainer}>
            <SiteReviewMigration />
          </Box>

          {/* Crew Cards Grid */}
          <Grid container spacing={3} className={classes.crewGrid}>
            {dashboardData.crews.map((crew) => (
              <Fade in key={crew.id} timeout={500}>
                <Grid item xs={12} md={6} lg={4}>
                  <CrewCard crewData={crew} />
                </Grid>
              </Fade>
            ))}
          </Grid>

          {/* No crews message */}
          {!loading && dashboardData.crews.length === 0 && (
            <Paper style={{ padding: 40, textAlign: 'center', marginTop: 20 }}>
              <Typography variant="h6" color="textSecondary">
                No crews found matching the selected filters
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Add rotating animation for refresh icon */}
      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Container>
  );
};

const mapStateToProps = (state) => ({
  entities: state.user?.entities || [], // Use user's assigned entities
  defaultEntity: state.user?.defaultEntity,
  user: state.user
});

export default connect(mapStateToProps)(AdminDashboard);